import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown, Plus, Trash2, AlertTriangle, Search } from "lucide-react";
import useTitanApp from "@/hooks/useTitanApp";
import { getCompanyVaccineByCompanyId } from "@/dispatchers/nmsDispatchers";
import type {
  TripType,
  TravelActivity,
} from "@/lib/travelHealth/types/travelHealth.types";
import {
  parseQuestionnaireSections1And2,
  parseQuestionnaireSections3And4,
  parseUkDateToIso,
  parseTripLengthDays,
  addDaysToIsoDate,
  extractKnownCountriesFromText,
} from "@/lib/travelHealth/questionnairePrefill";

// ─────────────────────────────────────────────────────────────────────────────
// COUNTRY LIST — for the destination combobox
// ─────────────────────────────────────────────────────────────────────────────

const COUNTRIES: string[] = [
  "Afghanistan", "Albania", "Algeria", "Andorra",
  "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas",
  "Bahrain", "Bangladesh", "Barbados", "Belarus",
  "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil",
  "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Central African Republic", "Chad", "Chile", "China",
  "Colombia", "Comoros", "Congo (Democratic Republic)",
  "Congo (Republic)", "Costa Rica", "Croatia", "Cuba",
  "Cyprus", "Czech Republic", "Denmark", "Djibouti",
  "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea",
  "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
  "Hungary", "Iceland", "India", "Indonesia", "Iran",
  "Iraq", "Ireland", "Israel", "Italy", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
  "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi",
  "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia",
  "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria",
  "North Korea", "North Macedonia", "Norway", "Oman",
  "Pakistan", "Palau", "Palestine", "Panama",
  "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia",
  "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa",
  "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone",
  "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan",
  "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden",
  "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga",
  "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu",
  "Vatican City", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe",
];

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL TYPES — data capture only, no clinical logic
// ─────────────────────────────────────────────────────────────────────────────

interface PriorVaccineEntry {
  id: string; // local uuid for list key
  productName: string;
  approximateDate: string; // free text e.g. "March 2022"
  doseNumber: string; // free text e.g. "1 of 3"
  givenElsewhere: boolean;
  notes: string;
}

interface Tier1AssessmentData {
  // Step 1
  tripDestinations: string[]; // one entry per destination
  tripTypes: TripType[];
  activities: TravelActivity[];
  departureDate: string; // ISO date string
  returnDate: string; // ISO date string
  tripNotes: string;
  accommodationType:
    | "good-standard"
    | "basic-budget"
    | "camping-hostels"
    | "staying-with-family"
    | "mixed-not-sure"
    | "";
  hasTravelInsurance: "yes" | "no" | "not-yet" | "";
  expectsToTravelAgain: "yes" | "no" | "not-sure" | "";

  // Step 2
  priorVaccines: PriorVaccineEntry[];
  priorAntimalarialName: string; // empty string if none
  priorAntimalarialTolerated: "yes" | "no" | "not-taken";
  priorAntimalarialNotes: string;
  holdsYellowFeverICVP: boolean | "unknown";

  // Step 3
  weightKg: string; // empty string if not captured
  isPregnant: boolean;
  couldBePregnant: boolean;
  planningPregnancy: boolean;
  isBreastfeeding: boolean;
  breastfeedingInfantAgeMonths: string; // empty string if not BF
  hasImmuneCondition: boolean;
  immuneConditionDetail: string;
  currentMedicines: string; // free text
  noMedications: boolean;
  allergies: {
    egg: boolean;
    latex: boolean;
    neomycinOrAminoglycoside: boolean;
    formaldehyde: boolean;
    gelatinOrPolygeline: boolean;
    sulphonamides: boolean;
    aMedicine: boolean;
    aFood: boolean;
    aPreviousVaccine: boolean;
    noneKnown: boolean;
    other: boolean;
    detail: string; // shared free-text field for medicine/food/vaccine/other
  };
  medicalHistory: {
    anaemia: boolean;
    bleedingOrClottingDisorder: boolean;
    heartDiseaseOrHighBloodPressure: boolean;
    diabetes: boolean;
    epilepsyOrSeizures: boolean;
    stomachOrBowelProblems: boolean;
    liverOrKidneyProblems: boolean;
    hivAids: boolean;
    immuneSystemCondition: boolean;
    mentalHealthCondition: boolean;
    neurologicalIllness: boolean;
    respiratoryCondition: boolean;
    rheumatologyOrJointCondition: boolean;
    spleenProblemsOrNoSpleen: boolean;
    previousSurgeryIncludingSpleenOrThymus: boolean;
    kidneyStones: boolean;
    glaucoma: boolean;
    porphyria: boolean;
    additionalNeedOrDisability: boolean;
    noneOfThese: boolean;
    other: boolean;
    otherDetail: string;
  };
  hivCd4Count: string; // digits only, "" if not provided
  isFeverishToday: boolean;

  // Step 4
  consentConfirmed: boolean;
  under16ConsentRoute:
    | "not-applicable"
    | "parental-responsibility"
    | "gillick-competent";
  anaphylaxisFacilitiesConfirmed: boolean;
}

interface TravelHealthTier1Props {
  patientId: string;
  patientAge: number; // in years, calculated before mount
  patientSex: string;
  siteId: string;
  questionnaireResponses?: { question: string; answer: string }[];
  initialWeightKg?: string;
  restoredState?: Tier1SavedState;
  onAutoSaveSnapshot?: (snapshot: Tier1SavedState) => void;
  onComplete: (data: Tier1AssessmentData) => void;
  onCancel: () => void;
}

interface Tier1SavedState {
  step: number;
  data: any;
  antimalarialTakenBefore: "yes" | "no" | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// STATIC OPTIONS
// ─────────────────────────────────────────────────────────────────────────────

const TRIP_TYPE_OPTIONS: { label: string; value: TripType }[] = [
  { label: "Holiday", value: "holiday" },
  { label: "Business trip", value: "business-trip" },
  { label: "Backpacking", value: "backpacking" },
  { label: "Hotel stay", value: "hotel-stay" },
  { label: "Cruise", value: "cruise" },
  { label: "Camping / hostels", value: "camping-hostels" },
  { label: "Living abroad (expatriate)", value: "living-abroad" },
  { label: "Safari", value: "safari" },
  { label: "Adventure travel", value: "adventure-travel" },
  { label: "Volunteer or relief work", value: "volunteer-relief-work" },
  { label: "Pilgrimage (Hajj / Umrah)", value: "hajj-umrah" },
  { label: "Diving", value: "diving" },
  { label: "Healthcare worker", value: "healthcare-worker" },
  {
    label: "Medical or dental treatment abroad",
    value: "medical-dental-treatment-abroad",
  },
  {
    label: "Visiting friends and relatives",
    value: "visiting-friends-relatives",
  },
];

const ACTIVITY_OPTIONS: { label: string; value: TravelActivity }[] = [
  {
    label: "Trekking or time at high altitude (over 2,500 m)",
    value: "trekking-high-altitude",
  },
  {
    label: "Swimming, rafting or contact with fresh water (lakes/rivers)",
    value: "freshwater-contact",
  },
  {
    label: "Contact with animals, including dogs, monkeys or bats",
    value: "animal-contact",
  },
  {
    label: "Cycling, running or being outdoors in remote areas",
    value: "outdoor-remote-areas",
  },
  {
    label: "Camping or sleeping outdoors",
    value: "camping-sleeping-outdoors",
  },
  {
    label: "Being outside at dawn, dusk or overnight",
    value: "outside-dawn-dusk-overnight",
  },
  {
    label: "Working in or visiting healthcare settings",
    value: "healthcare-settings",
  },
  { label: "Relief or aid work", value: "relief-aid-work" },
  { label: "Farming or working with animals", value: "farming-animals" },
  { label: "Caves or possible bat exposure", value: "caves-bat-exposure" },
  {
    label: "A mass gathering (e.g. Hajj/Umrah, festival, sporting event)",
    value: "mass-gathering",
  },
  {
    label: "Rural or remote areas, far from medical care",
    value: "rural-remote-far-from-care",
  },
  {
    label: "A long stay — more than one month",
    value: "long-stay-over-one-month",
  },
  {
    label: "Adventure or extreme sports",
    value: "adventure-extreme-sports",
  },
  {
    label: "Tattoos, piercings or cosmetic procedures abroad",
    value: "tattoos-piercings-cosmetic-abroad",
  },
  {
    label: "A new sexual partner while travelling",
    value: "new-sexual-partner",
  },
  {
    label: "A long-haul flight crossing 5 or more time zones",
    value: "long-haul-5plus-timezones",
  },
];

function labelsToTripTypes(labels: string[]): TripType[] {
  return TRIP_TYPE_OPTIONS.filter((opt) => labels.includes(opt.label)).map(
    (opt) => opt.value,
  );
}

function labelsToActivities(labels: string[]): TravelActivity[] {
  return ACTIVITY_OPTIONS.filter((opt) => labels.includes(opt.label)).map(
    (opt) => opt.value,
  );
}

function parseApproximateVaccineDate(text: string): Date | null {
  const trimmed = text.trim();

  // Bare 4-digit year — treat as 1 July of that year (a centred
  // estimate, since we don't know the actual month)
  const yearOnlyMatch = trimmed.match(/^(\d{4})$/);
  if (yearOnlyMatch) {
    const year = parseInt(yearOnlyMatch[1]);
    if (year > 1900 && year < 2100) {
      return new Date(year, 6, 1); // 1 July
    }
    return null;
  }

  // "Month YYYY" with a recognised month name
  const MONTH_NAMES: Record<string, number> = {
    january: 0, jan: 0, february: 1, feb: 1, march: 2, mar: 2,
    april: 3, apr: 3, may: 4, june: 5, jun: 5, july: 6, jul: 6,
    august: 7, aug: 7, september: 8, sep: 8, sept: 8,
    october: 9, oct: 9, november: 10, nov: 10,
    december: 11, dec: 11,
  };
  const monthYearMatch = trimmed.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthKey = monthYearMatch[1].toLowerCase();
    const year = parseInt(monthYearMatch[2]);
    if (
      monthKey in MONTH_NAMES &&
      year > 1900 && year < 2100
    ) {
      return new Date(year, MONTH_NAMES[monthKey], 1);
    }
    return null;
  }

  // Anything else — do not guess
  return null;
}

function isRevaxisBoosterLikelyDue(approximateDate: string): boolean {
  const parsed = parseApproximateVaccineDate(approximateDate);
  if (!parsed) return false;
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
  return parsed.getTime() < tenYearsAgo.getTime();
}

const PRIOR_VACCINE_OPTIONS: string[] = [
  "Tetanus / polio / diphtheria",
  "MMR",
  "Influenza (flu)",
  "Typhoid",
  "Hepatitis A",
  "Hepatitis B",
  "Pneumococcal",
  "Cholera",
  "Meningitis (ACWY or B)",
  "Rabies",
  "Japanese encephalitis",
  "Tick-borne encephalitis",
  "Yellow fever",
  "BCG (TB)",
  "COVID-19",
];

const STEP_TITLES: Record<number, string> = {
  1: "Trip and exposure",
  2: "Vaccination history",
  3: "Health screen",
  4: "Consent and competence",
};

function createLocalId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysUntil(dateIso: string): number | null {
  if (!dateIso) return null;
  const target = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(`${todayIsoDate()}T00:00:00`);
  const diffMs = target.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export function TravelHealthTier1Assessment({
  patientId,
  patientAge,
  patientSex,
  siteId,
  questionnaireResponses,
  initialWeightKg,
  restoredState,
  onAutoSaveSnapshot,
  onComplete,
  onCancel,
}: TravelHealthTier1Props) {
  const [step, setStep] = useState(() => restoredState?.step ?? 1);

  const { appState, nmsServiceState } = useTitanApp();
  const { companyId } = appState || {};
  const { companyVaccines } = nmsServiceState || {};

  useEffect(() => {
    if (companyId) getCompanyVaccineByCompanyId(companyId);
  }, [companyId]);

  const flattenedVaccineProducts = useMemo(() => {
    const results: { vaccineName: string; productName: string; uniqueId: string }[] = [];
    const seenIds = new Set<string>();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine && vaccine.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({
              vaccineName: vaccine.vaccineName,
              productName: product.productName,
              uniqueId,
            });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);

  const questionnairePrefill = useMemo(() => {
    if (!questionnaireResponses || questionnaireResponses.length === 0) {
      return null;
    }
    return {
      ...parseQuestionnaireSections1And2(questionnaireResponses),
      ...parseQuestionnaireSections3And4(questionnaireResponses),
    };
  }, [questionnaireResponses]);

  // The pre-consult form captures departure date and trip length as
  // free text, not validated date inputs. Only convert when the text
  // unambiguously matches the UK date convention (DD/MM/YYYY) or a
  // plain "N days" trip length — anything else is left unparsed so the
  // pharmacist enters it manually from the raw text shown below.
  const parsedDepartureDateIso = useMemo(
    () => parseUkDateToIso(questionnairePrefill?.departureDate),
    [questionnairePrefill?.departureDate],
  );
  const parsedTripLengthDays = useMemo(
    () => parseTripLengthDays(questionnairePrefill?.tripLengthDays),
    [questionnairePrefill?.tripLengthDays],
  );
  // Trip length is treated as inclusive of the departure day (e.g. a
  // "12 day trip" departing 10 Aug returns 21 Aug), per pharmacist
  // confirmation of the counting convention to use.
  const parsedReturnDateIso = useMemo(() => {
    if (!parsedDepartureDateIso || parsedTripLengthDays === undefined) {
      return undefined;
    }
    return addDaysToIsoDate(parsedDepartureDateIso, parsedTripLengthDays - 1);
  }, [parsedDepartureDateIso, parsedTripLengthDays]);

  // Only exact, known country names mentioned in the patient's free-text
  // answer are pre-filled — regional/urban-rural detail and per-leg
  // dates are not captured here and remain visible as raw text below.
  const parsedDestinations = useMemo(
    () =>
      extractKnownCountriesFromText(
        questionnairePrefill?.tripDestinationsRaw,
        COUNTRIES,
      ),
    [questionnairePrefill?.tripDestinationsRaw],
  );

  // Draft state widens under16ConsentRoute to include "" as the "not yet
  // selected" sentinel (consistent with the empty-string-for-unanswered
  // convention used elsewhere in this type, e.g. weightKg). The exported
  // Tier1AssessmentData interface itself is unchanged from spec — the
  // widened value is narrowed back before onComplete is called, which is
  // gated by isStep4Valid() to guarantee a real selection has been made.
  type Tier1AssessmentDraft = Omit<
    Tier1AssessmentData,
    | "under16ConsentRoute"
    | "holdsYellowFeverICVP"
    | "isPregnant"
    | "couldBePregnant"
    | "planningPregnancy"
    | "isBreastfeeding"
    | "hasImmuneCondition"
    | "isFeverishToday"
  > & {
    under16ConsentRoute: Tier1AssessmentData["under16ConsentRoute"] | "";
    // These yes/no questions default to unanswered (undefined) rather than
    // false, so the button pair starts with neither option selected and
    // the step's Next button stays disabled until a real choice is made.
    holdsYellowFeverICVP: boolean | "unknown" | undefined;
    isPregnant: boolean | undefined;
    couldBePregnant: boolean | undefined;
    planningPregnancy: boolean | undefined;
    isBreastfeeding: boolean | undefined;
    hasImmuneCondition: boolean | undefined;
    isFeverishToday: boolean | undefined;
  };

  // Computed once, on mount, from the patient's submitted pre-consult
  // form (if any). This intentionally only feeds the useState
  // initializer below — it must never be applied again after mount, so
  // it can never clobber an edit the pharmacist makes during the
  // session. isFeverishToday ("fit and well today", Q24) is never
  // included here: that question has no field in questionnairePrefill
  // at all, by design — it must always be asked fresh at supply.
  const [data, setData] = useState<Tier1AssessmentDraft>(() => {
    if (restoredState?.data) return restoredState.data as Tier1AssessmentDraft;
    return {
    tripDestinations: parsedDestinations,
    tripTypes: questionnairePrefill?.tripTypes
      ? labelsToTripTypes(questionnairePrefill.tripTypes)
      : [],
    activities: questionnairePrefill?.activities
      ? labelsToActivities(questionnairePrefill.activities)
      : [],
    // Only populated when the raw text unambiguously parsed (see
    // parsedDepartureDateIso/parsedReturnDateIso above) — otherwise left
    // blank and the raw text is surfaced separately for manual entry.
    departureDate: parsedDepartureDateIso ?? "",
    returnDate: parsedReturnDateIso ?? "",
    tripNotes: "",
    accommodationType: questionnairePrefill?.accommodationType ?? "",
    hasTravelInsurance: questionnairePrefill?.hasTravelInsurance ?? "",
    expectsToTravelAgain: questionnairePrefill?.expectsToTravelAgain ?? "",

    priorVaccines: questionnairePrefill?.priorVaccineNames?.length
      ? questionnairePrefill.priorVaccineNames.map((name) => ({
          id: createLocalId("vaccine"),
          productName: name,
          approximateDate: "",
          doseNumber: "",
          givenElsewhere: false,
          notes: "",
        }))
      : [],
    priorAntimalarialName: "",
    priorAntimalarialTolerated: "not-taken",
    priorAntimalarialNotes: "",
    holdsYellowFeverICVP: undefined,

    weightKg: questionnairePrefill?.weightKg || initialWeightKg || "",
    isPregnant:
      questionnairePrefill?.isPregnant ??
      (patientSex === "Male" ? false : undefined),
    couldBePregnant:
      questionnairePrefill?.couldBePregnant ??
      (patientSex === "Male" ? false : undefined),
    planningPregnancy: patientSex === "Male" ? false : undefined,
    isBreastfeeding:
      questionnairePrefill?.isBreastfeeding ??
      (patientSex === "Male" ? false : undefined),
    breastfeedingInfantAgeMonths: "",
    hasImmuneCondition: questionnairePrefill?.hasImmuneCondition ?? undefined,
    immuneConditionDetail: questionnairePrefill?.immuneConditionDetail ?? "",
    currentMedicines: questionnairePrefill?.currentMedicinesRaw ?? "",
    noMedications: false,
    allergies: questionnairePrefill?.allergies ?? {
      egg: false,
      latex: false,
      neomycinOrAminoglycoside: false,
      formaldehyde: false,
      gelatinOrPolygeline: false,
      sulphonamides: false,
      aMedicine: false,
      aFood: false,
      aPreviousVaccine: false,
      noneKnown: false,
      other: false,
      detail: "",
    },
    medicalHistory: questionnairePrefill?.medicalHistory ?? {
      anaemia: false,
      bleedingOrClottingDisorder: false,
      heartDiseaseOrHighBloodPressure: false,
      diabetes: false,
      epilepsyOrSeizures: false,
      stomachOrBowelProblems: false,
      liverOrKidneyProblems: false,
      hivAids: false,
      immuneSystemCondition: false,
      mentalHealthCondition: false,
      neurologicalIllness: false,
      respiratoryCondition: false,
      rheumatologyOrJointCondition: false,
      spleenProblemsOrNoSpleen: false,
      previousSurgeryIncludingSpleenOrThymus: false,
      kidneyStones: false,
      glaucoma: false,
      porphyria: false,
      additionalNeedOrDisability: false,
      noneOfThese: false,
      other: false,
      otherDetail: "",
    },
    hivCd4Count: questionnairePrefill?.hivCd4Count ?? "",
    // NEVER sourced from questionnairePrefill — see comment above.
    isFeverishToday: undefined,

    consentConfirmed: false,
    under16ConsentRoute: patientAge < 16 ? "" : "not-applicable",
    anaphylaxisFacilitiesConfirmed: false,
  };
  });

  const prefilledFieldNames = useMemo(() => {
    const set = new Set<string>();
    if (initialWeightKg) set.add("weightKg");
    if (!questionnairePrefill) return set;
    if (questionnairePrefill.weightKg) set.add("weightKg");
    if (parsedDepartureDateIso) set.add("departureDate");
    if (parsedReturnDateIso) set.add("returnDate");
    if (parsedDestinations.length > 0) set.add("tripDestinations");
    if (questionnairePrefill.accommodationType) set.add("accommodationType");
    if (questionnairePrefill.hasTravelInsurance) set.add("hasTravelInsurance");
    if (questionnairePrefill.expectsToTravelAgain) set.add("expectsToTravelAgain");
    if (questionnairePrefill.tripTypes?.length) set.add("tripTypes");
    if (questionnairePrefill.activities?.length) set.add("activities");
    if (questionnairePrefill.priorVaccineNames?.length) set.add("priorVaccines");
    if (
      questionnairePrefill.priorAntimalarialTolerated === "not-taken" ||
      questionnairePrefill.priorAntimalarialNotesRaw
    ) {
      set.add("antimalarialTakenBefore");
    }
    if (questionnairePrefill.allergies) set.add("allergies");
    if (
      questionnairePrefill.isPregnant !== undefined ||
      questionnairePrefill.couldBePregnant !== undefined ||
      questionnairePrefill.isBreastfeeding !== undefined
    ) {
      set.add("pregnancy");
    }
    if (questionnairePrefill.hasImmuneCondition !== undefined) {
      set.add("immuneCondition");
    }
    if (questionnairePrefill.medicalHistory) set.add("medicalHistory");
    if (questionnairePrefill.hivCd4Count) set.add("hivCd4Count");
    if (questionnairePrefill.currentMedicinesRaw) set.add("currentMedicines");
    return set;
  }, [
    questionnairePrefill,
    parsedDepartureDateIso,
    parsedReturnDateIso,
    parsedDestinations,
  ]);

  const FromPatientFormBadge = () => (
    <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
      From patient form — verify
    </span>
  );

  // Step 1 field-level "touched" state — each error shows as soon as the
  // pharmacist leaves (blurs) that field, not only when Next is pressed.
  const [destinationsTouched, setDestinationsTouched] = useState(false);
  const [departureDateTouched, setDepartureDateTouched] = useState(false);
  const [returnDateTouched, setReturnDateTouched] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryPopoverOpen, setCountryPopoverOpen] = useState(false);
  const [otherVaccineName, setOtherVaccineName] = useState("");
  const [otherVaccineAdded, setOtherVaccineAdded] = useState(false);
  const [otherVaccineQuery, setOtherVaccineQuery] = useState("");
  const [otherVaccineSearchOpen, setOtherVaccineSearchOpen] = useState(false);
  const otherVaccineDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        otherVaccineDropdownRef.current &&
        !otherVaccineDropdownRef.current.contains(event.target as Node)
      ) {
        setOtherVaccineSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOtherVaccineProducts = useMemo(() => {
    const query = otherVaccineQuery.trim().toLowerCase();
    if (!query) return flattenedVaccineProducts;
    return flattenedVaccineProducts.filter((product) =>
      `${product.vaccineName} ${product.productName}`
        .toLowerCase()
        .includes(query),
    );
  }, [flattenedVaccineProducts, otherVaccineQuery]);

  // Local-only UI state for the outer "have you taken antimalarial tablets
  // before?" question. The Tier1AssessmentData fields only capture whether
  // the antimalarial was tolerated (priorAntimalarialTolerated) — this local
  // state drives which of the three outer options is selected and whether
  // the nested "which antimalarial / did you tolerate it" fields are shown.
  // Prefilled from Q22 ("No" -> "no"; any "Yes" answer -> "yes", since the
  // patient explicitly ticked having taken antimalarials before — the
  // *which one* and *did you tolerate it* detail is free text and is
  // surfaced separately for the pharmacist to transcribe, never guessed).
  const [antimalarialTakenBefore, setAntimalarialTakenBefore] = useState<
    "yes" | "no" | undefined
  >(() => {
    if (restoredState?.antimalarialTakenBefore !== undefined) {
      return restoredState.antimalarialTakenBefore;
    }
    if (questionnairePrefill?.priorAntimalarialTolerated === "not-taken") {
      return "no";
    }
    if (questionnairePrefill?.priorAntimalarialNotesRaw) {
      return "yes";
    }
    return undefined;
  });

  const antimalarialRawNotesBanner = questionnairePrefill?.priorAntimalarialNotesRaw && (
    <div className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-md p-2">
      <span className="font-medium">As given by patient:</span>{" "}
      {questionnairePrefill.priorAntimalarialNotesRaw}
      <p className="text-xs text-blue-600 mt-1">
        Please use this to complete the fields below.
      </p>
    </div>
  );

  const updateField = <K extends keyof Tier1AssessmentDraft>(
    field: K,
    value: Tier1AssessmentDraft[K],
  ) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const onAutoSaveSnapshotRef = useRef(onAutoSaveSnapshot);
  onAutoSaveSnapshotRef.current = onAutoSaveSnapshot;

  useEffect(() => {
    onAutoSaveSnapshotRef.current?.({ step, data, antimalarialTakenBefore });
  }, [step, data, antimalarialTakenBefore]);

  const isUnder16 = patientAge < 16;
  const showPregnancyQuestions = patientSex !== "Male";

  const handleCd4Change = (raw: string) => {
    const digitsOnly = raw.replace(/[^0-9]/g, "").slice(0, 4);
    updateField("hivCd4Count", digitsOnly);
  };

  // ── Step 1 helpers ──────────────────────────────────────────────────────

  const addDestination = (country: string) => {
    if (!country || data.tripDestinations.includes(country)) return;
    updateField("tripDestinations", [...data.tripDestinations, country]);
  };

  const removeDestination = (index: number) => {
    updateField(
      "tripDestinations",
      data.tripDestinations.filter((_, i) => i !== index),
    );
  };

  const toggleTripType = (value: TripType) => {
    updateField(
      "tripTypes",
      data.tripTypes.includes(value)
        ? data.tripTypes.filter((t) => t !== value)
        : [...data.tripTypes, value],
    );
  };

  const toggleActivity = (value: TravelActivity) => {
    updateField(
      "activities",
      data.activities.includes(value)
        ? data.activities.filter((a) => a !== value)
        : [...data.activities, value],
    );
  };

  const leadTimeDays = useMemo(
    () => daysUntil(data.departureDate),
    [data.departureDate],
  );

  const isStep1Valid = (): boolean => {
    if (data.tripDestinations.length === 0) return false;
    if (!data.departureDate) return false;
    if (data.departureDate < todayIsoDate()) return false;
    if (!data.returnDate) return false;
    if (data.returnDate <= data.departureDate) return false;
    return true;
  };

  // ── Step 2 helpers ──────────────────────────────────────────────────────

  const isVaccineSelected = (name: string): boolean =>
    data.priorVaccines.some((v) => v.productName === name);

  const toggleStandardVaccine = (name: string) => {
    if (isVaccineSelected(name)) {
      updateField(
        "priorVaccines",
        data.priorVaccines.filter((v) => v.productName !== name),
      );
    } else {
      const entry: PriorVaccineEntry = {
        id: createLocalId("vaccine"),
        productName: name,
        approximateDate: "",
        doseNumber: "",
        givenElsewhere: false,
        notes: "",
      };
      updateField("priorVaccines", [...data.priorVaccines, entry]);
    }
  };

  const updateVaccineDetail = (
    id: string,
    field: keyof Omit<PriorVaccineEntry, "id" | "productName">,
    value: string | boolean,
  ) => {
    updateField(
      "priorVaccines",
      data.priorVaccines.map((v) =>
        v.id === id ? { ...v, [field]: value } : v,
      ),
    );
  };

  const addOtherVaccine = () => {
    if (!otherVaccineName.trim()) return;
    const entry: PriorVaccineEntry = {
      id: createLocalId("vaccine"),
      productName: otherVaccineName.trim(),
      approximateDate: "",
      doseNumber: "",
      givenElsewhere: false,
      notes: "",
    };
    updateField("priorVaccines", [...data.priorVaccines, entry]);
    setOtherVaccineName("");
    setOtherVaccineAdded(true);
  };

  const removeVaccineEntry = (id: string) => {
    updateField(
      "priorVaccines",
      data.priorVaccines.filter((entry) => entry.id !== id),
    );
  };

  const isStep2Valid = (): boolean => {
    if (antimalarialTakenBefore === undefined) return false;
    if (
      antimalarialTakenBefore === "yes" &&
      data.priorAntimalarialTolerated !== "yes" &&
      data.priorAntimalarialTolerated !== "no"
    ) {
      return false;
    }
    if (data.holdsYellowFeverICVP === undefined) return false;
    return true;
  };

  // ── Step 3 helpers ──────────────────────────────────────────────────────


  const isStep3Valid = (): boolean => {
    if (showPregnancyQuestions) {
      if (data.isPregnant === undefined) return false;
      if (data.isPregnant === false && data.couldBePregnant === undefined) {
        return false;
      }
      if (data.planningPregnancy === undefined) return false;
      if (data.isBreastfeeding === undefined) return false;
    }
    if (data.hasImmuneCondition === undefined) return false;
    if (data.isFeverishToday === undefined) return false;
    if (!data.currentMedicines.trim() && !data.noMedications) return false;
    const anyConditionTicked =
      data.medicalHistory.anaemia ||
      data.medicalHistory.bleedingOrClottingDisorder ||
      data.medicalHistory.heartDiseaseOrHighBloodPressure ||
      data.medicalHistory.diabetes ||
      data.medicalHistory.epilepsyOrSeizures ||
      data.medicalHistory.stomachOrBowelProblems ||
      data.medicalHistory.liverOrKidneyProblems ||
      data.medicalHistory.hivAids ||
      data.medicalHistory.immuneSystemCondition ||
      data.medicalHistory.mentalHealthCondition ||
      data.medicalHistory.neurologicalIllness ||
      data.medicalHistory.respiratoryCondition ||
      data.medicalHistory.rheumatologyOrJointCondition ||
      data.medicalHistory.spleenProblemsOrNoSpleen ||
      data.medicalHistory.previousSurgeryIncludingSpleenOrThymus ||
      data.medicalHistory.kidneyStones ||
      data.medicalHistory.glaucoma ||
      data.medicalHistory.porphyria ||
      data.medicalHistory.additionalNeedOrDisability ||
      data.medicalHistory.other ||
      data.medicalHistory.noneOfThese;
    if (!anyConditionTicked) return false;
    const anyAllergyTicked =
      data.allergies.egg ||
      data.allergies.latex ||
      data.allergies.neomycinOrAminoglycoside ||
      data.allergies.formaldehyde ||
      data.allergies.gelatinOrPolygeline ||
      data.allergies.sulphonamides ||
      data.allergies.aMedicine ||
      data.allergies.aFood ||
      data.allergies.aPreviousVaccine ||
      data.allergies.other ||
      data.allergies.noneKnown;
    if (!anyAllergyTicked) return false;
    return true;
  };

  // ── Step 4 helpers ──────────────────────────────────────────────────────

  const isStep4Valid = (): boolean => {
    if (!data.consentConfirmed) return false;
    if (!data.anaphylaxisFacilitiesConfirmed) return false;
    if (
      isUnder16 &&
      data.under16ConsentRoute !== "parental-responsibility" &&
      data.under16ConsentRoute !== "gillick-competent"
    ) {
      return false;
    }
    return true;
  };

  // ── Navigation ───────────────────────────────────────────────────────────

  const handleNext = () => {
    if (step === 1 && !isStep1Valid()) return;
    if (step === 2 && !isStep2Valid()) return;
    if (step === 3 && !isStep3Valid()) return;
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleFinish = () => {
    if (!isStep4Valid()) return;
    // isStep4Valid guarantees under16ConsentRoute has been narrowed away
    // from the "" draft sentinel to a real Tier1AssessmentData value.
    onComplete(data as Tier1AssessmentData);
  };

  return (
    <div className="p-6 space-y-6">
      {questionnairePrefill && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-2 mb-4">
          <p className="text-sm font-semibold text-blue-900">
            From the patient's pre-consult form
          </p>
          {questionnairePrefill.sex && questionnairePrefill.sex !== patientSex && (
            <div className="bg-amber-100 border border-amber-300 rounded p-2">
              <p className="text-sm text-amber-800">
                ⚠ Patient record has sex as "{patientSex}" but the
                pre-consult form was answered as "{questionnairePrefill.sex}".
                Please confirm with the patient.
              </p>
            </div>
          )}
          {questionnairePrefill.fullName && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Name given:</span> {questionnairePrefill.fullName}
            </p>
          )}
          {questionnairePrefill.dateOfBirth && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">DOB given:</span> {questionnairePrefill.dateOfBirth}
            </p>
          )}
          {questionnairePrefill.email && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Email given:</span> {questionnairePrefill.email}
            </p>
          )}
          {questionnairePrefill.phone && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Phone given:</span> {questionnairePrefill.phone}
            </p>
          )}
          {questionnairePrefill.homeAddress && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Address given:</span> {questionnairePrefill.homeAddress}
            </p>
          )}
          {questionnairePrefill.gpPracticeAndName && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">GP given:</span> {questionnairePrefill.gpPracticeAndName}
            </p>
          )}
          {questionnairePrefill.countryOfOrigin && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Country of origin given:</span> {questionnairePrefill.countryOfOrigin}
            </p>
          )}
          {questionnairePrefill.tripDestinationsRaw && (
            <div className="text-sm text-blue-800">
              <span className="font-medium">Destinations, as described by patient:</span>
              <p className="mt-1 whitespace-pre-wrap">{questionnairePrefill.tripDestinationsRaw}</p>
              <p className="text-xs text-blue-600 mt-1">
                {parsedDestinations.length > 0
                  ? "The countries mentioned above have been added below — please check they're correct and add any missing stopovers."
                  : "Please enter structured destinations below based on this."}
              </p>
            </div>
          )}
          {questionnairePrefill.departureDate && !parsedDepartureDateIso && (
            <div className="text-sm text-blue-800">
              <span className="font-medium">Departure date, as given by patient:</span>{" "}
              {questionnairePrefill.departureDate}
              <p className="text-xs text-blue-600 mt-1">
                This didn't match a recognised date format — please verify
                and enter it in the date field below.
              </p>
            </div>
          )}
          {questionnairePrefill.tripLengthDays &&
            (!parsedDepartureDateIso || parsedTripLengthDays === undefined) && (
              <div className="text-sm text-blue-800">
                <span className="font-medium">Trip length, as given by patient:</span>{" "}
                {questionnairePrefill.tripLengthDays}
                <p className="text-xs text-blue-600 mt-1">
                  Please use this to work out and enter the return date below.
                </p>
              </div>
            )}
          {questionnairePrefill.priorVaccineNotesRaw && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Vaccine history notes:</span> {questionnairePrefill.priorVaccineNotesRaw}
            </p>
          )}
          {questionnairePrefill.currentMedicinesRaw && (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Medicines given:</span> {questionnairePrefill.currentMedicinesRaw}
            </p>
          )}
          {questionnairePrefill.needsPeriodDelay === "yes" ? (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Period delay interest (from form):</span> Yes
            </p>
          ) : questionnairePrefill.needsPeriodDelay === "no" ? (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Period delay interest (from form):</span> No
            </p>
          ) : questionnairePrefill.needsPeriodDelay === "prefer-not-to-say" ? (
            <p className="text-sm text-blue-800">
              <span className="font-medium">Period delay interest (from form):</span> Prefer not to say
            </p>
          ) : null}
          {questionnairePrefill.additionalPatientNotes.length > 0 && (
            <div className="space-y-1">
              {questionnairePrefill.additionalPatientNotes.map((note, i) => (
                <p key={i} className="text-sm text-blue-800">
                  <span className="font-medium">{note.label}:</span> {note.text}
                </p>
              ))}
            </div>
          )}
          <p className="text-xs text-blue-600 pt-1">
            Verify all of the above with the patient — this is what they
            submitted, not confirmed clinical fact.
          </p>
        </div>
      )}

      {!questionnaireResponses && (
        <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-sm text-gray-600">
            No pre-consult form was received from this patient. All
            questions below must be asked fresh.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Step {step} of 4</p>
          <h2 className="text-lg font-semibold text-gray-900">
            {STEP_TITLES[step]}
          </h2>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>
              Which countries are you travelling to, including any stopovers
              or onward travel?
              {prefilledFieldNames.has("tripDestinations") && (
                <FromPatientFormBadge />
              )}
            </Label>
            <Popover
              open={countryPopoverOpen}
              onOpenChange={(open) => {
                setCountryPopoverOpen(open);
                if (!open) setDestinationsTouched(true);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={countryPopoverOpen}
                  className="w-full justify-between font-normal"
                >
                  Search for a country...
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] max-h-[300px] p-0"
                align="start"
                sideOffset={4}
              >
                <Command>
                  <CommandInput
                    placeholder="Search for a country..."
                    value={countrySearch}
                    onValueChange={setCountrySearch}
                  />
                  <CommandList className="max-h-[300px] overflow-y-auto">
                    <CommandEmpty>No country found.</CommandEmpty>
                    {COUNTRIES.map((country) => {
                      const alreadyAdded = data.tripDestinations.includes(country);
                      return (
                        <CommandItem
                          key={country}
                          value={country}
                          disabled={alreadyAdded}
                          onSelect={() => {
                            if (alreadyAdded) return;
                            addDestination(country);
                            setCountryPopoverOpen(false);
                            setCountrySearch("");
                          }}
                        >
                          {alreadyAdded && (
                            <Check className="mr-2 h-4 w-4 text-green-600" />
                          )}
                          <span className={alreadyAdded ? "text-gray-400" : undefined}>
                            {country}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {data.tripDestinations.length > 0 && (
              <ul className="space-y-1">
                {data.tripDestinations.map((destination, index) => (
                  <li
                    key={`${destination}-${index}`}
                    className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                  >
                    <span>{destination}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        removeDestination(index);
                        setDestinationsTouched(true);
                      }}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {destinationsTouched && data.tripDestinations.length === 0 && (
              <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                Please add at least one destination.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="departure-date">
                When do you leave?
                {prefilledFieldNames.has("departureDate") && <FromPatientFormBadge />}
              </Label>
              <Input
                id="departure-date"
                type="date"
                min={todayIsoDate()}
                value={data.departureDate}
                onChange={(e) => updateField("departureDate", e.target.value)}
                onBlur={() => setDepartureDateTouched(true)}
              />
              {departureDateTouched &&
                (!data.departureDate ||
                  data.departureDate < todayIsoDate()) && (
                  <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                    Please enter a departure date that is today or in the
                    future.
                  </p>
                )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="return-date">
                When do you return?
                {prefilledFieldNames.has("returnDate") && <FromPatientFormBadge />}
              </Label>
              <Input
                id="return-date"
                type="date"
                min={data.departureDate || todayIsoDate()}
                value={data.returnDate}
                onChange={(e) => updateField("returnDate", e.target.value)}
                onBlur={() => setReturnDateTouched(true)}
              />
              {returnDateTouched &&
                (!data.returnDate ||
                  data.returnDate <= data.departureDate) && (
                  <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                    Return date must be after the departure date.
                  </p>
                )}
            </div>
          </div>

          {leadTimeDays !== null && (
            <p className="text-sm text-gray-600">
              Days until departure: {leadTimeDays}
            </p>
          )}

          <div className="space-y-2">
            <Label>
              Where will you be staying, and what will food and water be
              like?
              {prefilledFieldNames.has("accommodationType") && <FromPatientFormBadge />}
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                variant={
                  data.accommodationType === "good-standard"
                    ? "default"
                    : "outline"
                }
                onClick={() => updateField("accommodationType", "good-standard")}
              >
                Good-standard accommodation
              </Button>
              <Button
                type="button"
                variant={
                  data.accommodationType === "basic-budget"
                    ? "default"
                    : "outline"
                }
                onClick={() => updateField("accommodationType", "basic-budget")}
              >
                Basic or budget accommodation
              </Button>
              <Button
                type="button"
                variant={
                  data.accommodationType === "camping-hostels"
                    ? "default"
                    : "outline"
                }
                onClick={() =>
                  updateField("accommodationType", "camping-hostels")
                }
              >
                Camping or hostels
              </Button>
              <Button
                type="button"
                variant={
                  data.accommodationType === "staying-with-family"
                    ? "default"
                    : "outline"
                }
                onClick={() =>
                  updateField("accommodationType", "staying-with-family")
                }
              >
                Staying with a local family
              </Button>
              <Button
                type="button"
                variant={
                  data.accommodationType === "mixed-not-sure"
                    ? "default"
                    : "outline"
                }
                onClick={() =>
                  updateField("accommodationType", "mixed-not-sure")
                }
              >
                Mixed / not sure
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Do you have travel insurance?
              {prefilledFieldNames.has("hasTravelInsurance") && <FromPatientFormBadge />}
            </Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={data.hasTravelInsurance === "yes" ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("hasTravelInsurance", "yes")}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={data.hasTravelInsurance === "no" ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("hasTravelInsurance", "no")}
              >
                No
              </Button>
              <Button
                type="button"
                variant={
                  data.hasTravelInsurance === "not-yet" ? "default" : "outline"
                }
                className="flex-1"
                onClick={() => updateField("hasTravelInsurance", "not-yet")}
              >
                Not yet
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Do you expect to travel abroad again in the future?
              {prefilledFieldNames.has("expectsToTravelAgain") && <FromPatientFormBadge />}
            </Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={
                  data.expectsToTravelAgain === "yes" ? "default" : "outline"
                }
                className="flex-1"
                onClick={() => updateField("expectsToTravelAgain", "yes")}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={
                  data.expectsToTravelAgain === "no" ? "default" : "outline"
                }
                className="flex-1"
                onClick={() => updateField("expectsToTravelAgain", "no")}
              >
                No
              </Button>
              <Button
                type="button"
                variant={
                  data.expectsToTravelAgain === "not-sure"
                    ? "default"
                    : "outline"
                }
                className="flex-1"
                onClick={() => updateField("expectsToTravelAgain", "not-sure")}
              >
                Not sure
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              What kind of trip is it? Tick everything that applies.
              {prefilledFieldNames.has("tripTypes") && <FromPatientFormBadge />}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TRIP_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 border rounded-md px-3 py-2"
                >
                  <Checkbox
                    id={`trip-type-${option.value}`}
                    checked={data.tripTypes.includes(option.value)}
                    onCheckedChange={() => toggleTripType(option.value)}
                  />
                  <Label
                    htmlFor={`trip-type-${option.value}`}
                    className="cursor-pointer font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              What will you be doing while you're away? Tick everything that
              applies.
              {prefilledFieldNames.has("activities") && <FromPatientFormBadge />}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ACTIVITY_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 border rounded-md px-3 py-2"
                >
                  <Checkbox
                    id={`activity-${option.value}`}
                    checked={data.activities.includes(option.value)}
                    onCheckedChange={() => toggleActivity(option.value)}
                  />
                  <Label
                    htmlFor={`activity-${option.value}`}
                    className="cursor-pointer font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="trip-notes">
              Activities and accommodation notes (optional)
            </Label>
            <Textarea
              id="trip-notes"
              value={data.tripNotes}
              onChange={(e) => updateField("tripNotes", e.target.value)}
              rows={3}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="space-y-3">
            <Label>
              Which of these vaccines have you had before? Tick all that
              apply.
              {prefilledFieldNames.has("priorVaccines") && <FromPatientFormBadge />}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {PRIOR_VACCINE_OPTIONS.map((option) => (
                <div
                  key={option}
                  className="flex items-center space-x-2 border rounded-md px-3 py-2"
                >
                  <Checkbox
                    id={`prior-vaccine-${option}`}
                    checked={isVaccineSelected(option)}
                    onCheckedChange={() => toggleStandardVaccine(option)}
                  />
                  <Label
                    htmlFor={`prior-vaccine-${option}`}
                    className="cursor-pointer font-normal"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </div>

            {data.priorVaccines
              .filter((entry) => PRIOR_VACCINE_OPTIONS.includes(entry.productName))
              .map((entry) => (
                <div
                  key={entry.id}
                  className="border rounded-lg p-3 space-y-2"
                >
                  <p className="text-sm font-medium text-gray-900">
                    {entry.productName}
                    {entry.productName === "Tetanus / polio / diphtheria" &&
                      isRevaxisBoosterLikelyDue(entry.approximateDate) && (
                        <span className="text-xs text-amber-700 ml-2">
                          Booster likely due (10+ years since last dose,
                          based on the date given)
                        </span>
                      )}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      placeholder='e.g. "2019" or "March 2022"'
                      value={entry.approximateDate}
                      onChange={(e) =>
                        updateVaccineDetail(
                          entry.id,
                          "approximateDate",
                          e.target.value,
                        )
                      }
                    />
                    <Input
                      placeholder='e.g. "1 of 3"'
                      value={entry.doseNumber}
                      onChange={(e) =>
                        updateVaccineDetail(
                          entry.id,
                          "doseNumber",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`given-elsewhere-${entry.id}`}
                      checked={entry.givenElsewhere}
                      onCheckedChange={(checked) =>
                        updateVaccineDetail(
                          entry.id,
                          "givenElsewhere",
                          checked === true,
                        )
                      }
                    />
                    <Label
                      htmlFor={`given-elsewhere-${entry.id}`}
                      className="font-normal"
                    >
                      Given elsewhere
                    </Label>
                  </div>
                </div>
              ))}

            <div className="space-y-2 pt-2">
              <Label>
                Any other vaccine not listed above?
              </Label>
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative" ref={otherVaccineDropdownRef}>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                    <Input
                      className="pl-9"
                      placeholder="e.g. Yellow fever, Rabies"
                      value={otherVaccineSearchOpen ? otherVaccineQuery : otherVaccineName}
                      onChange={(e) => {
                        setOtherVaccineQuery(e.target.value);
                        setOtherVaccineSearchOpen(true);
                      }}
                      onFocus={() => setOtherVaccineSearchOpen(true)}
                    />
                  </div>
                  {otherVaccineSearchOpen && (
                    <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-[240px] overflow-auto z-50">
                      {filteredOtherVaccineProducts.length === 0 ? (
                        <div className="py-4 text-sm text-center text-gray-500">
                          No vaccines found.
                        </div>
                      ) : (
                        filteredOtherVaccineProducts.map((product) => (
                          <button
                            key={product.uniqueId}
                            type="button"
                            onClick={() => {
                              setOtherVaccineName(
                                `${product.vaccineName} - ${product.productName}`,
                              );
                              setOtherVaccineQuery("");
                              setOtherVaccineSearchOpen(false);
                            }}
                            className="w-full flex flex-col items-start px-3 py-2 text-sm text-left hover:bg-gray-100"
                          >
                            <span className="font-medium">{product.vaccineName}</span>
                            <span className="text-xs opacity-60">{product.productName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={addOtherVaccine}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {data.priorVaccines.filter(
              (entry) => !PRIOR_VACCINE_OPTIONS.includes(entry.productName),
            ).length > 0 && (
              <ul className="space-y-2">
                {data.priorVaccines
                  .filter(
                    (entry) => !PRIOR_VACCINE_OPTIONS.includes(entry.productName),
                  )
                  .map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-start justify-between border rounded-md px-3 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium">{entry.productName}</p>
                        <p className="text-gray-600">
                          {[
                            entry.approximateDate,
                            entry.doseNumber,
                            entry.givenElsewhere ? "Given elsewhere" : undefined,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                        {entry.notes && (
                          <p className="text-gray-500">{entry.notes}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVaccineEntry(entry.id)}
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              Have you taken antimalarial tablets before?
              {prefilledFieldNames.has("antimalarialTakenBefore") && (
                <FromPatientFormBadge />
              )}
            </Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={antimalarialTakenBefore === "yes" ? "default" : "outline"}
                className="flex-1"
                onClick={() => setAntimalarialTakenBefore("yes")}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={antimalarialTakenBefore === "no" ? "default" : "outline"}
                className="flex-1"
                onClick={() => {
                  setAntimalarialTakenBefore("no");
                  updateField("priorAntimalarialName", "");
                  updateField("priorAntimalarialTolerated", "not-taken");
                  updateField("priorAntimalarialNotes", "");
                }}
              >
                No
              </Button>
            </div>

            {antimalarialTakenBefore === "yes" && (
              <div className="space-y-3 pl-1 pt-2">
                {antimalarialRawNotesBanner}
                <div className="space-y-2">
                  <Label htmlFor="antimalarial-name">
                    Which antimalarial?
                  </Label>
                  <Input
                    id="antimalarial-name"
                    value={data.priorAntimalarialName}
                    onChange={(e) =>
                      updateField("priorAntimalarialName", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Did you tolerate them?</Label>
                  <div className="flex gap-3 mt-2">
                    <Button
                      type="button"
                      variant={
                        data.priorAntimalarialTolerated === "yes" ? "default" : "outline"
                      }
                      className="flex-1"
                      onClick={() =>
                        updateField("priorAntimalarialTolerated", "yes")
                      }
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={
                        data.priorAntimalarialTolerated === "no" ? "default" : "outline"
                      }
                      className="flex-1"
                      onClick={() =>
                        updateField("priorAntimalarialTolerated", "no")
                      }
                    >
                      No
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="antimalarial-tolerated-notes">Notes</Label>
                  <Textarea
                    id="antimalarial-tolerated-notes"
                    value={data.priorAntimalarialNotes}
                    onChange={(e) =>
                      updateField("priorAntimalarialNotes", e.target.value)
                    }
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              Do you already hold a yellow fever vaccination certificate
              (ICVP)?
            </Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={data.holdsYellowFeverICVP === true ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("holdsYellowFeverICVP", true)}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={data.holdsYellowFeverICVP === false ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("holdsYellowFeverICVP", false)}
              >
                No
              </Button>
              <Button
                type="button"
                variant={data.holdsYellowFeverICVP === "unknown" ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("holdsYellowFeverICVP", "unknown")}
              >
                Unknown
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">Patient age: {patientAge} years</p>

          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <Label htmlFor="weight-kg">
                Weight (kg) — required if antimalarials or altitude medication
                are being considered, or for children
              </Label>
              {prefilledFieldNames.has("weightKg") && (
                questionnairePrefill?.weightKg ? (
                  <FromPatientFormBadge />
                ) : (
                  <span className="text-xs font-normal text-green-600 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                    From vitals — verify
                  </span>
                )
              )}
            </div>
            <Input
              id="weight-kg"
              type="number"
              value={data.weightKg}
              onChange={(e) => updateField("weightKg", e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Leave blank if not required. You will be prompted again if an
              antimalarial is selected.
            </p>
          </div>

          <div className="space-y-4">
            {showPregnancyQuestions && (
              <div className="space-y-2">
                <Label>
                  Is the patient pregnant?
                  {prefilledFieldNames.has("pregnancy") && <FromPatientFormBadge />}
                </Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={data.isPregnant === true ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("isPregnant", true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={data.isPregnant === false ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("isPregnant", false)}
                  >
                    No
                  </Button>
                </div>
              </div>
            )}

            {showPregnancyQuestions && data.isPregnant === false && (
              <div className="space-y-2">
                <Label>Could the patient be pregnant?</Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={data.couldBePregnant === true ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("couldBePregnant", true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={data.couldBePregnant === false ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("couldBePregnant", false)}
                  >
                    No
                  </Button>
                </div>
              </div>
            )}

            {showPregnancyQuestions && (
              <div className="space-y-2">
                <Label>Is the patient planning a pregnancy?</Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={data.planningPregnancy === true ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("planningPregnancy", true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={data.planningPregnancy === false ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("planningPregnancy", false)}
                  >
                    No
                  </Button>
                </div>
              </div>
            )}

            {showPregnancyQuestions && (
              <div className="space-y-2">
                <Label>Is the patient breastfeeding?</Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={data.isBreastfeeding === true ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("isBreastfeeding", true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={data.isBreastfeeding === false ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => updateField("isBreastfeeding", false)}
                  >
                    No
                  </Button>
                </div>
              </div>
            )}

            {showPregnancyQuestions && data.isBreastfeeding && (
              <div className="space-y-2 pl-1">
                <Label htmlFor="infant-age-months">
                  How old is the infant (months)?
                </Label>
                <Input
                  id="infant-age-months"
                  type="number"
                  value={data.breastfeedingInfantAgeMonths}
                  onChange={(e) =>
                    updateField("breastfeedingInfantAgeMonths", e.target.value)
                  }
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="space-y-2">
              <Label>
                Does the patient have any condition, or take any medicine,
                that affects the immune system? (e.g. high-dose steroids,
                biologics, chemotherapy, HIV). Spleen problems are asked
                about separately.
                {prefilledFieldNames.has("immuneCondition") && <FromPatientFormBadge />}
              </Label>
              <div className="flex gap-3 mt-2">
                <Button
                  type="button"
                  variant={data.hasImmuneCondition === true ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => updateField("hasImmuneCondition", true)}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={data.hasImmuneCondition === false ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => updateField("hasImmuneCondition", false)}
                >
                  No
                </Button>
              </div>
            </div>
            {data.hasImmuneCondition && (
              <div className="space-y-2 pl-1">
                <Label htmlFor="immune-condition-detail">
                  Please provide details
                </Label>
                <Textarea
                  id="immune-condition-detail"
                  value={data.immuneConditionDetail}
                  onChange={(e) =>
                    updateField("immuneConditionDetail", e.target.value)
                  }
                  rows={2}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="current-medicines">
              What medicines does the patient currently take, including
              anything bought without a prescription or any herbal remedy
              such as St John's Wort?
              {prefilledFieldNames.has("currentMedicines") && <FromPatientFormBadge />}
            </Label>
            <Textarea
              id="current-medicines"
              value={data.currentMedicines}
              onChange={(e) => updateField("currentMedicines", e.target.value)}
              disabled={data.noMedications}
              rows={3}
            />
            <div className="flex items-start space-x-2">
              <Checkbox
                id="no-medications"
                checked={data.noMedications}
                onCheckedChange={(checked) =>
                  updateField("noMedications", checked === true)
                }
              />
              <Label htmlFor="no-medications" className="font-normal">
                No current medications — this question was asked and the patient
                takes nothing, including OTC products and herbal remedies
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Do you have any allergies? Tick anything that applies.
              {prefilledFieldNames.has("allergies") && <FromPatientFormBadge />}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                [
                  { key: "egg", label: "Egg" },
                  { key: "latex", label: "Latex" },
                  {
                    key: "neomycinOrAminoglycoside",
                    label: "Neomycin or other aminoglycoside antibiotics",
                  },
                  { key: "formaldehyde", label: "Formaldehyde" },
                  { key: "gelatinOrPolygeline", label: "Gelatin / polygeline" },
                  {
                    key: "sulphonamides",
                    label:
                      "Sulphonamides (\"sulfa\" antibiotics or water tablets)",
                  },
                  {
                    key: "aMedicine",
                    label: "A medicine — please give details below",
                  },
                  {
                    key: "aFood",
                    label: "A food — please give details below",
                  },
                  {
                    key: "aPreviousVaccine",
                    label: "A previous vaccine — please give details below",
                  },
                  { key: "noneKnown", label: "None that I know of" },
                  {
                    key: "other",
                    label: "Other — please give details below",
                  },
                ] as const
              ).map((option) => (
                <div
                  key={option.key}
                  className="flex items-center space-x-2 border rounded-md px-3 py-2"
                >
                  <Checkbox
                    id={`allergy-${option.key}`}
                    checked={data.allergies[option.key]}
                    onCheckedChange={(checked) =>
                      updateField("allergies", {
                        ...data.allergies,
                        [option.key]: checked === true,
                      })
                    }
                  />
                  <Label
                    htmlFor={`allergy-${option.key}`}
                    className="cursor-pointer font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>

            {(data.allergies.aMedicine ||
              data.allergies.aFood ||
              data.allergies.aPreviousVaccine ||
              data.allergies.other) && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="allergy-detail">
                  Please give details (including any reaction).
                </Label>
                <Textarea
                  id="allergy-detail"
                  value={data.allergies.detail}
                  onChange={(e) =>
                    updateField("allergies", {
                      ...data.allergies,
                      detail: e.target.value,
                    })
                  }
                  rows={2}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              Do any of these apply to you, now or in the past? Tick all that
              apply.
              {prefilledFieldNames.has("medicalHistory") && <FromPatientFormBadge />}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                [
                  { key: "anaemia", label: "Anaemia" },
                  {
                    key: "bleedingOrClottingDisorder",
                    label:
                      "Bleeding or clotting disorder (including DVT / blood clots)",
                  },
                  {
                    key: "heartDiseaseOrHighBloodPressure",
                    label: "Heart disease or high blood pressure",
                  },
                  { key: "diabetes", label: "Diabetes" },
                  {
                    key: "epilepsyOrSeizures",
                    label: "Epilepsy or seizures (you or a close relative)",
                  },
                  {
                    key: "stomachOrBowelProblems",
                    label: "Stomach or bowel problems",
                  },
                  {
                    key: "liverOrKidneyProblems",
                    label: "Liver or kidney problems",
                  },
                  { key: "hivAids", label: "HIV / AIDS" },
                  {
                    key: "immuneSystemCondition",
                    label: "An immune-system condition (e.g. a blood cancer)",
                  },
                  {
                    key: "mentalHealthCondition",
                    label:
                      "A mental health condition — anxiety or depression, now or in the past",
                  },
                  {
                    key: "neurologicalIllness",
                    label: "A neurological (nervous system) illness",
                  },
                  {
                    key: "respiratoryCondition",
                    label: "A respiratory (breathing) condition",
                  },
                  {
                    key: "rheumatologyOrJointCondition",
                    label: "A rheumatology or joint condition",
                  },
                  {
                    key: "spleenProblemsOrNoSpleen",
                    label: "Spleen problems, or no spleen",
                  },
                  {
                    key: "previousSurgeryIncludingSpleenOrThymus",
                    label:
                      "Previous surgery (including spleen or thymus removal)",
                  },
                  { key: "kidneyStones", label: "Kidney stones" },
                  { key: "glaucoma", label: "Glaucoma" },
                  { key: "porphyria", label: "Porphyria" },
                  {
                    key: "additionalNeedOrDisability",
                    label: "An additional need or disability",
                  },
                  { key: "noneOfThese", label: "None of these" },
                  { key: "other", label: "Other" },
                ] as const
              ).map((option) => (
                <div
                  key={option.key}
                  className="flex items-center space-x-2 border rounded-md px-3 py-2"
                >
                  <Checkbox
                    id={`medical-history-${option.key}`}
                    checked={data.medicalHistory[option.key]}
                    onCheckedChange={(checked) =>
                      updateField("medicalHistory", {
                        ...data.medicalHistory,
                        [option.key]: checked === true,
                      })
                    }
                  />
                  <Label
                    htmlFor={`medical-history-${option.key}`}
                    className="cursor-pointer font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>

            {data.medicalHistory.hivAids && (
              <div className="space-y-2 ml-6">
                <Label htmlFor="hiv-cd4-count">
                  Most recent CD4 count, if known (cells/µL)
                  {prefilledFieldNames.has("hivCd4Count") && <FromPatientFormBadge />}
                </Label>
                <Input
                  id="hiv-cd4-count"
                  inputMode="numeric"
                  value={data.hivCd4Count}
                  onChange={(e) => handleCd4Change(e.target.value)}
                  placeholder="Leave blank if not sure"
                  className="w-32"
                />
              </div>
            )}

            {data.medicalHistory.other && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="medical-history-other-detail">
                  Please give details.
                </Label>
                <Textarea
                  id="medical-history-other-detail"
                  value={data.medicalHistory.otherDetail}
                  onChange={(e) =>
                    updateField("medicalHistory", {
                      ...data.medicalHistory,
                      otherDetail: e.target.value,
                    })
                  }
                  rows={2}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Is the patient feeling unwell or feverish today?</Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={data.isFeverishToday === true ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("isFeverishToday", true)}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={data.isFeverishToday === false ? "default" : "outline"}
                className="flex-1"
                onClick={() => updateField("isFeverishToday", false)}
              >
                No
              </Button>
            </div>
          </div>

          {data.isFeverishToday && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Vaccination must be deferred</AlertTitle>
              <AlertDescription>
                The patient is currently unwell or feverish. Do not proceed
                with this consultation today. Document the deferral and
                rebook the appointment.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="consent-confirmed"
              checked={data.consentConfirmed}
              onCheckedChange={(checked) =>
                updateField("consentConfirmed", checked === true)
              }
            />
            <Label htmlFor="consent-confirmed" className="font-normal">
              Valid informed consent has been obtained, or a best-interests
              decision under the Mental Capacity Act 2005 has been
              documented.
            </Label>
          </div>

          {isUnder16 && (
            <div className="space-y-2">
              <Label>Consent for this patient under 16 was obtained via:</Label>
              <RadioGroup
                value={data.under16ConsentRoute}
                onValueChange={(value) =>
                  updateField(
                    "under16ConsentRoute",
                    value as Tier1AssessmentData["under16ConsentRoute"],
                  )
                }
                className="flex flex-col gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="parental-responsibility"
                    id="consent-parental"
                  />
                  <Label htmlFor="consent-parental" className="font-normal">
                    Person with parental responsibility
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="gillick-competent"
                    id="consent-gillick"
                  />
                  <Label htmlFor="consent-gillick" className="font-normal">
                    Patient assessed as Gillick competent
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="flex items-start space-x-2">
            <Checkbox
              id="anaphylaxis-confirmed"
              checked={data.anaphylaxisFacilitiesConfirmed}
              onCheckedChange={(checked) => {
                if (data.anaphylaxisFacilitiesConfirmed && checked !== true) {
                  return;
                }
                updateField("anaphylaxisFacilitiesConfirmed", checked === true);
              }}
            />
            <Label htmlFor="anaphylaxis-confirmed" className="font-normal">
              Facilities for management of anaphylaxis and immediate access
              to adrenaline (epinephrine) 1 in 1,000 are available at this
              premises.
            </Label>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4 border-t border-gray-200">
        <div className="flex gap-2">
          <Button variant="outline" onClick={step === 1 ? onCancel : handleBack}>
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step > 1 && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>

        {step === 3 && data.isFeverishToday ? (
          <Button
            onClick={onCancel}
            className="bg-slate-900 hover:bg-slate-800"
          >
            Close and document deferral
          </Button>
        ) : step < 4 ? (
          <Button
            onClick={() => {
              if (step === 1) {
                setDestinationsTouched(true);
                setDepartureDateTouched(true);
                setReturnDateTouched(true);
              }
              handleNext();
            }}
            disabled={
              (step === 2 && !isStep2Valid()) ||
              (step === 3 && !isStep3Valid())
            }
            className="bg-slate-900 hover:bg-slate-800"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleFinish}
            disabled={!isStep4Valid()}
            className="bg-slate-900 hover:bg-slate-800"
          >
            Finish
          </Button>
        )}
      </div>
    </div>
  );
}

export type { Tier1AssessmentData, Tier1SavedState, PriorVaccineEntry, TravelHealthTier1Props };
